/**
 * MealPlans — weekly meal calendar with allergen visibility.
 *
 * Allergens are surfaced on every meal card (sourced from each child's
 * Admissions profile via the /allergy-alerts endpoint when staff review
 * the room).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { AdminHeader } from "./_shared";

const MEAL_TYPES = ["breakfast", "snack_morning", "lunch", "snack_afternoon"];

function thisMonday() {
  const d = new Date();
  const day = d.getDay();
  const diff = (day === 0 ? 6 : day - 1);
  d.setDate(d.getDate() - diff);
  return d.toISOString().slice(0, 10);
}

export default function MealPlans() {
  const { t } = useI18n();
  const [weekStart, setWeekStart] = useState(thisMonday());
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/ops/meal-plans/${weekStart}`);
      setPlan(r.data);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart]);

  const submit = async (form) => {
    try {
      await api.post(`/admin/ops/meal-plans/${weekStart}`, {
        date: form.date,
        meal_type: form.meal_type,
        name: form.name,
        ingredients: form.ingredients.split(",").map((s) => s.trim()).filter(Boolean),
        allergens: form.allergens.split(",").map((s) => s.trim()).filter(Boolean),
        notes: form.notes || null,
      });
      toast.success(t("ops.common.saved"));
      setModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ops.errors.generic"));
    }
  };

  return (
    <main className="p-6 lg:p-10 space-y-6" data-testid="admin-meal-plans">
      <AdminHeader
        eyebrow={t("ops.meal_plans.eyebrow")}
        title={t("ops.meal_plans.title")}
        action={
          <label className="text-sm text-[#1A1B2E] inline-flex items-center gap-2">
            <span className="eyebrow">{t("ops.meal_plans.week_picker")}</span>
            <input
              type="date"
              value={weekStart}
              onChange={(e) => setWeekStart(e.target.value)}
              data-testid="meal-plans-week-picker"
              className="px-3 py-1.5 border border-[#EEF0F4] rounded-lg text-sm bg-white"
            />
          </label>
        }
      />
      <p className="text-sm text-[#6B7280] max-w-2xl">{t("ops.meal_plans.blurb")}</p>

      {loading || !plan ? (
        <p className="font-serif italic text-[#525252]">{t("ops.common.loading")}</p>
      ) : (
        <section className="grid grid-cols-1 lg:grid-cols-7 gap-3" data-testid="meal-plans-grid">
          {plan.days.map((day) => {
            const d = new Date(day.date);
            const dow = d.toLocaleDateString(undefined, { weekday: "short" });
            return (
              <div key={day.date} className="rounded-2xl bg-white border border-[#EEF0F4] p-4" data-testid={`meal-day-${day.date}`}>
                <div className="eyebrow">{dow} · {day.date.slice(5)}</div>
                <ul className="mt-3 space-y-2">
                  {MEAL_TYPES.map((mt) => (
                    <li key={mt}>
                      <button
                        onClick={() => setModal({
                          date: day.date,
                          meal_type: mt,
                          name: day[mt]?.name || "",
                          ingredients: (day[mt]?.ingredients || []).join(", "),
                          allergens: (day[mt]?.allergens || []).join(", "),
                          notes: day[mt]?.notes || "",
                        })}
                        data-testid={`meal-slot-${day.date}-${mt}`}
                        className="w-full text-left rounded-xl border border-[#EEF0F4] p-2.5 hover:bg-[#FAFAFB] transition-colors"
                      >
                        <div className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#6B7280]">
                          {t(`ops.meal_type.${mt}`)}
                        </div>
                        {day[mt] ? (
                          <>
                            <div className="text-sm text-[#1A1B2E] mt-0.5">{day[mt].name}</div>
                            {(day[mt].allergens || []).length > 0 && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {day[mt].allergens.map((a) => (
                                  <span key={a} className="inline-block text-[10px] tracking-[0.05em] font-medium text-[#A11D1D] bg-[#FCEEEE] rounded-full px-2 py-0.5">
                                    ⚠ {a}
                                  </span>
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-xs italic text-[#9CA3AF] mt-0.5">{t("ops.meal_plans.no_meal_set")}</div>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </section>
      )}

      {modal && (
        <div role="dialog" aria-modal="true" data-testid="meal-plan-modal" onClick={() => setModal(null)} className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl text-[#1A1B2E]">
              {t("ops.meal_plans.edit_meal_title")} — {t(`ops.meal_type.${modal.meal_type}`)}
            </h3>
            <div className="mt-5 space-y-3">
              <label className="block">
                <span className="eyebrow">{t("ops.meal_plans.meal_name_label")}</span>
                <input type="text" value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })} data-testid="meal-modal-name" className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm" />
              </label>
              <label className="block">
                <span className="eyebrow">{t("ops.meal_plans.ingredients_label")}</span>
                <input type="text" value={modal.ingredients} onChange={(e) => setModal({ ...modal, ingredients: e.target.value })} data-testid="meal-modal-ingredients" className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm" />
              </label>
              <label className="block">
                <span className="eyebrow">{t("ops.meal_plans.allergens_label")}</span>
                <input type="text" value={modal.allergens} onChange={(e) => setModal({ ...modal, allergens: e.target.value })} data-testid="meal-modal-allergens" className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm" />
              </label>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button onClick={() => setModal(null)} className="text-sm text-[#6B7280] hover:text-[#1A1B2E]">{t("ops.common.cancel")}</button>
              <button onClick={() => submit(modal)} data-testid="meal-modal-submit" className="inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]">{t("ops.meal_plans.submit")}</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
